import React, { useState, useRef, useEffect } from 'react';
import { SyncSettings, SyncStatus, Album } from '../types';

import * as LuIcons from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';

const { LuX, LuCloud, LuSave, LuDownload, LuUpload, LuRefreshCw, LuWand2, LuAlertCircle } = LuIcons;

const DEFAULT_WEB_APP_URL = 'https://script.google.com/macros/s/AKfycby1CQ2HwX5taSfZ1M3h8u6dVt99bNhM5GZRBqmcRlvfLPcdgRhwwFE-FTmxqbYzZHKf9A/exec';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  settings: SyncSettings;
  lastSyncAt: number | null;
  onSave: (settings: SyncSettings) => void;
  status: SyncStatus;
  onManualSync: () => void;
  albums: Album[];
  onImport: (albums: Album[], settings?: SyncSettings) => void;
}

export function SyncSettingsModal({ isOpen, onClose, settings, lastSyncAt, onSave, status, onManualSync, albums, onImport }: Props) {
  const [localSettings, setLocalSettings] = useState<SyncSettings>(settings);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLocalSettings(settings);
    setError(null);
  }, [settings, isOpen]);

  if (!isOpen) return null;

  const validate = () => {
    if (localSettings.scriptUrl && !localSettings.scriptUrl.startsWith('https://script.google.com/')) {
      return 'Google Apps Script URL must start with https://script.google.com/';
    }
    return null;
  };

  const handleSave = () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    onSave(localSettings);
  };

  const handleAutoFill = () => {
    setLocalSettings(prev => ({
      ...prev,
      scriptUrl: DEFAULT_WEB_APP_URL
    }));
    setError(null);
  };

  const handleExport = () => {
    const data = {
      settings: localSettings,
      albums
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `linklens_backup_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target?.result as string);
        if (data.albums && Array.isArray(data.albums)) {
          onImport(data.albums, data.settings);
        }
      } catch (err) {
        alert('Failed to parse backup file');
      }
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const canSync = localSettings.scriptUrl.trim() !== '' && status !== 'syncing';

  let displayStatus: string = status;
  if (!settings.scriptUrl) {
    displayStatus = 'not_configured';
  } else if (status === 'idle') {
    displayStatus = 'ready';
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col max-h-[90vh] border border-slate-800">
        <div className="flex items-center justify-between p-5 border-b border-slate-800">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <SafeIcon icon={LuCloud} className="w-5 h-5 text-blue-400" />
            Sync Settings
          </h2>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors">
            <SafeIcon icon={LuX} className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-5 overflow-y-auto flex-1 space-y-5">
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 flex items-start gap-3">
              <SafeIcon icon={LuAlertCircle} className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
              <p className="text-sm text-red-400 font-medium">{error}</p>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="block text-sm font-semibold text-slate-300">Google Apps Script URL</label>
            <div className="relative">
              <input
                type="text"
                value={localSettings.scriptUrl}
                onChange={e => {
                  setLocalSettings({...localSettings, scriptUrl: e.target.value});
                  setError(null);
                }}
                placeholder="https://script.google.com/macros/s/.../exec"
                className="w-full pl-3 pr-24 py-2.5 bg-slate-950 border border-slate-800 text-white rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none text-sm transition-all placeholder:text-slate-600"
              />
              <button
                onClick={handleAutoFill}
                className="absolute right-1.5 top-1.5 bottom-1.5 px-3 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 text-xs font-semibold rounded-lg transition-colors flex items-center gap-1.5"
                title="Auto-Fill Default URL"
              >
                <SafeIcon icon={LuWand2} className="w-3.5 h-3.5" />
                Auto-Fill
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="block text-sm font-semibold text-slate-300">Device Label (Optional)</label>
            <input
              type="text"
              value={localSettings.deviceLabel}
              onChange={e => setLocalSettings({...localSettings, deviceLabel: e.target.value})}
              placeholder="e.g. My Laptop"
              className="w-full px-3 py-2.5 bg-slate-950 border border-slate-800 text-white rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none text-sm transition-all placeholder:text-slate-600"
            />
          </div>

          <div className="pt-2">
            <label className="flex items-center gap-3 cursor-pointer group">
              <div className="relative flex items-center justify-center">
                <input
                  type="checkbox"
                  checked={localSettings.autoSync}
                  onChange={e => setLocalSettings({...localSettings, autoSync: e.target.checked})}
                  className="peer sr-only"
                />
                <div className="w-10 h-6 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
              </div>
              <span className="text-sm font-medium text-slate-300 group-hover:text-white transition-colors">Enable Auto Sync</span>
            </label>
            <p className="text-xs text-slate-500 mt-2 ml-13">Auto sync runs every 10 minutes while this app is open.</p>
          </div>

          <div className="pt-5 border-t border-slate-800">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-slate-400">Status</span>
              <span className={`text-xs font-bold px-2.5 py-1 rounded-md uppercase tracking-wider ${
                displayStatus === 'syncing' ? 'bg-blue-500/20 text-blue-400' :
                displayStatus === 'success' || displayStatus === 'ready' ? 'bg-green-500/20 text-green-400' :
                displayStatus === 'error' ? 'bg-red-500/20 text-red-400' :
                'bg-slate-800 text-slate-400'
              }`}>
                {displayStatus === 'success' ? 'SYNCED' : displayStatus.replace('_', ' ')}
              </span>
            </div>
            {lastSyncAt && (
              <p className="text-xs text-slate-500">Last sync: {new Date(lastSyncAt).toLocaleString()}</p>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={handleSave}
              className="flex-1 px-4 py-2.5 bg-slate-800 text-white font-semibold rounded-xl hover:bg-slate-700 transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <SafeIcon icon={LuSave} className="w-4 h-4" />
              Save Settings
            </button>
            <button
              onClick={onManualSync}
              disabled={!canSync}
              className="flex-1 px-4 py-2.5 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-500 disabled:bg-slate-800 disabled:text-slate-500 transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <SafeIcon icon={LuRefreshCw} className={`w-4 h-4 ${status === 'syncing' ? 'animate-spin' : ''}`} />
              Sync Now
            </button>
          </div>

          <div className="pt-5 border-t border-slate-800 space-y-3">
            <h3 className="text-sm font-semibold text-slate-400">Local Backup</h3>
            <div className="flex gap-3">
              <button
                onClick={handleExport}
                className="flex-1 px-3 py-2.5 bg-slate-900 border border-slate-800 text-slate-300 font-medium rounded-xl hover:bg-slate-800 hover:text-white transition-colors flex items-center justify-center gap-2 text-sm"
              >
                <SafeIcon icon={LuDownload} className="w-4 h-4" />
                Export JSON
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex-1 px-3 py-2.5 bg-slate-900 border border-slate-800 text-slate-300 font-medium rounded-xl hover:bg-slate-800 hover:text-white transition-colors flex items-center justify-center gap-2 text-sm"
              >
                <SafeIcon icon={LuUpload} className="w-4 h-4" />
                Import JSON
              </button>
              <input type="file" accept=".json" ref={fileInputRef} onChange={handleImport} className="hidden" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}